import OnboardingLayout from "component/auth/OnboardingLayout";
import { fetchCurrentBusiness } from "api/fetchers";
import {
    handleFetchKybAvailableWallets,
    handleUpdateBusinessFinancialInformation,
} from "api/onboarding";
import Dropdown from "component/form/dropDown";
import FileUploadTile from "component/form/FileUploadTile";
import MultiSelectDropdown from "component/form/MultiSelectDropdown";
import FailHandler from "component/failhandler";
import { InfoCircle } from "iconsax-reactjs";
import InfoTooltip from "component/InfoTooltip";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import Loader from "component/loader";
import { formatMoney } from "utilities/formatMoney";

const steps = [
    { title: "Business information" },
    { title: "Business verification", subtitles: ["Compliance", "Wallets"] },
    { title: "Business ownership" },
];

const fallbackWallets = [
    "NGN",
    "EUR",
    "GBP",
    "USD",
    "USDT",
    "USDC",
    "KES",
    "UGX",
    "GHS",
    "CAD",
    "XOF",
    "XAF",
];

const walletMeta = {
    NGN: { flag: "🇳🇬", label: "NG Naira" },
    EUR: { flag: "🇪🇺", label: "EU Euro" },
    GBP: { flag: "🇬🇧", label: "GB Pounds" },
    USD: { flag: "🇺🇸", label: "US Dollars" },
    USDT: { flag: "₮", label: "USDT" },
    USDC: { flag: "$", label: "USDC" },
    KES: { flag: "🇰🇪", label: "KE Shillings" },
    UGX: { flag: "🇺🇬", label: "UG Shillings" },
    GHS: { flag: "🇬🇭", label: "GH Cedis" },
    CAD: { flag: "🇨🇦", label: "CA Dollars" },
    XOF: { flag: "🌍", label: "XOF Francs" },
    XAF: { flag: "🌍", label: "XAF Francs" },
};

const getWalletCurrency = (wallet) =>
    String(wallet?.currency || wallet?.code || wallet?.value || wallet || "").toUpperCase();

const getWalletLabel = (wallet) => {
    const code = getWalletCurrency(wallet);
    const apiLabel = wallet?.name || wallet?.label || wallet?.currencyName || wallet?.title;

    if (apiLabel && apiLabel !== code) return apiLabel;
    return walletMeta[code]?.label || code;
};

const getWalletFlag = (wallet) => {
    const code = getWalletCurrency(wallet);
    return wallet?.flag || walletMeta[code]?.flag || code.slice(0, 1);
};

const formatWalletOption = (wallet) => {
    const code = getWalletCurrency(wallet);
    const logo = wallet?.logo;

    return {
        value: code,
        label: (
            <span className="flex items-center gap-2">
                {logo ? (
                    <img src={logo} alt="" className="size-5 shrink-0 rounded-full object-cover" />
                ) : (
                    <span>{getWalletFlag(wallet)}</span>
                )}
                <span>{getWalletLabel(wallet)}</span>
            </span>
        ),
    };
};

const normalizeWalletList = (response) => {
    const raw =
        (Array.isArray(response) && response) ||
        (Array.isArray(response?.wallets) && response.wallets) ||
        (Array.isArray(response?.data) && response.data) ||
        (Array.isArray(response?.data?.wallets) && response.data.wallets) ||
        (Array.isArray(response?.availableWallets) && response.availableWallets) ||
        (Array.isArray(response?.data?.availableWallets) && response.data.availableWallets) ||
        [];

    const seen = new Set();

    return raw.filter((wallet) => {
        const currency = getWalletCurrency(wallet);
        if (!currency || seen.has(currency)) return false;
        seen.add(currency);
        return true;
    });
};

const getBusinessCountryCode = (business) =>
    String(business?.businessCountry?.code || business?.countryCode || "").toUpperCase();

const isNigerianBusinessRecord = (business) => {
    const countryName = String(
        business?.businessCountry?.name || business?.country || ""
    ).toLowerCase();

    return getBusinessCountryCode(business) === "NG" || countryName === "nigeria";
};

const getBusinessDefaultWalletCurrency = (business) => {
    return isNigerianBusinessRecord(business) ? "NGN" : "USD";
};

const formatCurrencyOption = (wallet) => {
    const code = getWalletCurrency(wallet);
    const logo = wallet?.logo;

    return {
        value: code,
        label: (
            <span className="flex items-center gap-2">
                {logo ? (
                    <img src={logo} alt="" className="size-5 shrink-0 rounded-full object-cover" />
                ) : (
                    <span>{getWalletFlag(wallet)}</span>
                )}
                <span>{code}</span>
            </span>
        ),
    };
};

const fallbackVolumeCurrencyOptions = fallbackWallets.map(formatCurrencyOption);

const WEBSITE_PATTERN = /^(https?:\/\/)?([a-z0-9-]+\.)+[a-z]{2,}(\/[^\s]*)?$/i;

const SOURCE_OF_FUNDS_OPTIONS = [
    { value: "salary_wages", label: "Salary/wages" },
    { value: "business_revenue", label: "Business revenue" },
    { value: "investment", label: "Investment" },
    { value: "sales_of_assets", label: "Sales of assets" },
    { value: "loan_credit_line", label: "Loan / Credit line" },
    { value: "other_sources", label: "Other sources" },
];

const SOURCE_OF_FUNDS_DOC_TYPE_OPTIONS_BY_SOURCE = {
    salary_wages: [
        { value: "payslip", label: "Payslip" },
        { value: "tax_returns", label: "Tax returns" },
        { value: "employment_contract", label: "Employment contract" },
        { value: "bank_statement", label: "Bank statement" },
    ],
    business_revenue: [
        { value: "invoice", label: "Invoice" },
        { value: "signed_contract", label: "Signed contract" },
        { value: "bank_statement", label: "Bank statement" },
    ],
    investment: [
        { value: "investment_agreement", label: "Investment agreement" },
        { value: "grant_letter", label: "Grant letter" },
        { value: "bank_transfer_receipt", label: "Bank transfer receipt" },
    ],
    sales_of_assets: [
        { value: "sale_agreement", label: "Sale agreement" },
        { value: "title_transfer_documents", label: "Title transfer documents" },
        { value: "bank_statement", label: "Bank statement" },
    ],
    loan_credit_line: [
        { value: "loan_agreement", label: "Loan agreement" },
        { value: "proof_of_disbursement", label: "Proof of disbursement" },
        { value: "lender_details", label: "Lender details" },
    ],
    other_sources: [
        { value: "bank_statement", label: "Bank statement" },
        { value: "supporting_document", label: "Supporting document" },
        { value: "other", label: "Other" },
    ],
};

const getUploadedUrl = (doc) =>
    (typeof doc === "string" && doc) ||
    doc?.url ||
    doc?.url?.link ||
    doc?.link ||
    doc?.data?.url ||
    doc?.data?.url?.link ||
    doc?.data?.link ||
    "";

const normalizeUrl = (raw) => {
    const value = String(raw || "").trim();
    if (!value) return "";
    return /^https?:\/\//i.test(value) ? value : `https://${value}`;
};

const getSourceOfFundName = (sourceOfFunds) => {
    if (sourceOfFunds === "salary_wages") return "salary";
    if (sourceOfFunds === "business_revenue") return "business_revenue";
    return sourceOfFunds;
};

const normalizeOptionValue = (value, options) => {
    const normalized = String(value || "")
        .trim()
        .toLowerCase()
        .replace(/[\s/-]+/g, "_");
    return (
        options.find((option) => {
            const optionValue = String(option.value).toLowerCase();
            const optionLabel = String(option.label)
                .toLowerCase()
                .replace(/[\s/-]+/g, "_");
            return optionValue === normalized || optionLabel === normalized;
        })?.value || ""
    );
};

const BusinessVerificationWallets = ({ currentStep = 1 }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const shouldReturnToSummary =
        new URLSearchParams(location.search).get("returnTo") === "summary";
    const [businessId, setBusinessId] = useState("");
    const [businessType, setBusinessType] = useState("");
    const [businessCountryCode, setBusinessCountryCode] = useState("");
    const [availableWallets, setAvailableWallets] = useState([]);
    const [walletOptions, setWalletOptions] = useState(fallbackWallets.map(formatWalletOption));
    const [volumeCurrencyOptions, setVolumeCurrencyOptions] = useState(
        fallbackVolumeCurrencyOptions
    );
    const [selectedWallets, setSelectedWallets] = useState([]);
    const [volumeCurrency, setVolumeCurrency] = useState("NGN");
    const [averageMonthlyVolume, setAverageMonthlyVolume] = useState("");
    const [website, setWebsite] = useState("");
    const [sourceOfFunds, setSourceOfFunds] = useState("");
    const [sourceDocumentType, setSourceDocumentType] = useState("");
    const [sourceOfFundsDoc, setSourceOfFundsDoc] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [fails, setFails] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [validationErrors, setValidationErrors] = useState({});

    useEffect(() => {
        const getBusinessData = async () => {
            try {
                const [data, availableWalletsResponse] = await Promise.all([
                    fetchCurrentBusiness(),
                    handleFetchKybAvailableWallets(),
                ]);
                const business = data?.business || data;
                const defaultWalletCurrency = getBusinessDefaultWalletCurrency(business);
                const countryCode = getBusinessCountryCode(business);
                const isNigerianBusiness = isNigerianBusinessRecord(business);
                const offeredWallets = normalizeWalletList(availableWalletsResponse);
                const fallbackOfferedWallets = normalizeWalletList(data?.allWallets);
                const allAvailableWallets = offeredWallets.length
                    ? offeredWallets
                    : fallbackOfferedWallets;
                const selectableWallets = (
                    allAvailableWallets.length ? allAvailableWallets : fallbackWallets
                ).filter((wallet) => {
                    const currency = getWalletCurrency(wallet);
                    if (currency === defaultWalletCurrency) return false;
                    if (!isNigerianBusiness && currency === "USD") return false;
                    return true;
                });
                const selectableCurrencies = selectableWallets.map(getWalletCurrency);
                const currentWallets = (business?.wallets || [])
                    .map(getWalletCurrency)
                    .filter(
                        (currency) =>
                            currency &&
                            currency !== defaultWalletCurrency &&
                            selectableCurrencies.includes(currency)
                    );

                setBusinessId(business?._id || "");
                setBusinessType(business?.type || "");
                setBusinessCountryCode(countryCode || (isNigerianBusiness ? "NG" : "NON_NG"));
                setAvailableWallets(
                    allAvailableWallets.length ? allAvailableWallets : fallbackWallets
                );
                setWalletOptions(
                    (selectableWallets.length
                        ? selectableWallets
                        : fallbackWallets.filter((currency) => {
                              if (currency === defaultWalletCurrency) return false;
                              if (!isNigerianBusiness && currency === "USD") return false;
                              return true;
                          })
                    ).map(formatWalletOption)
                );
                setVolumeCurrencyOptions(
                    (allAvailableWallets.length ? allAvailableWallets : fallbackWallets).map(
                        formatCurrencyOption
                    )
                );
                setSelectedWallets(currentWallets);
                const monthlyVolume = business?.monthlyTransactionVolume || {};
                const sourceDocument = business?.sourceOfFund || business?.sourceOfFunds || null;
                const sourceValue = normalizeOptionValue(
                    sourceDocument?.type || sourceDocument?.source || sourceDocument?.name,
                    SOURCE_OF_FUNDS_OPTIONS
                );
                const documentOptions =
                    SOURCE_OF_FUNDS_DOC_TYPE_OPTIONS_BY_SOURCE[sourceValue] || [];
                const documentValue = normalizeOptionValue(
                    sourceDocument?.documentType || sourceDocument?.name,
                    documentOptions
                );

                setVolumeCurrency(monthlyVolume?.currency || defaultWalletCurrency || "NGN");
                setAverageMonthlyVolume(
                    monthlyVolume?.amount ? formatMoney(String(monthlyVolume.amount)) : ""
                );
                setWebsite(business?.website || business?.businessWebsite || "");
                setSourceOfFunds(sourceValue);
                setSourceDocumentType(documentValue);
                if (getUploadedUrl(sourceDocument)) {
                    setSourceOfFundsDoc({
                        name:
                            sourceDocument?.name ||
                            sourceDocument?.key ||
                            "Source of funds document",
                        url: getUploadedUrl(sourceDocument),
                        fileId: sourceDocument?.fileId || sourceDocument?.key || "",
                    });
                }
            } catch (error) {
                console.error("Failed to fetch current business:", error);
            }
        };

        getBusinessData();
    }, []);

    const handleBack = () => {
        if (businessType === "SoleProprietorship") {
            navigate("/onboarding/business-verification/compliance/soleproprietorship");
            return;
        }

        navigate("/onboarding/business-verification/compliance/llc");
    };

    const handleVolumeChange = (value) => {
        const numericValue = value.replace(/\D/g, "");
        setAverageMonthlyVolume(numericValue ? formatMoney(numericValue) : "");
        setValidationErrors((current) => ({ ...current, averageMonthlyVolume: "" }));
    };

    const hasBusinessCountryCode = !!businessCountryCode;
    const isNigerianBusiness = businessCountryCode === "NG";
    const usdWalletSelected = selectedWallets.includes("USD");
    const requiresWebsite = hasBusinessCountryCode && (!isNigerianBusiness || usdWalletSelected);
    const requiresSourceOfFunds =
        hasBusinessCountryCode && (!isNigerianBusiness || usdWalletSelected);
    const requiresAverageMonthlyVolume =
        hasBusinessCountryCode && (!isNigerianBusiness || usdWalletSelected);
    const averageMonthlyVolumeComplete =
        !requiresAverageMonthlyVolume || !!averageMonthlyVolume.replace(/,/g, "").trim();
    const cleanWebsite = website.trim();
    const websiteIsValid = !cleanWebsite || WEBSITE_PATTERN.test(cleanWebsite);
    const websiteComplete = websiteIsValid && (!requiresWebsite || Boolean(cleanWebsite));
    const sourceDocumentTypeOptions =
        SOURCE_OF_FUNDS_DOC_TYPE_OPTIONS_BY_SOURCE[sourceOfFunds] || [];
    const selectedSourceDocumentType =
        sourceDocumentTypeOptions.find((option) => option.value === sourceDocumentType) || null;
    const selectedSourceOfFunds =
        SOURCE_OF_FUNDS_OPTIONS.find((option) => option.value === sourceOfFunds) || null;
    const sourceOfFundsComplete = Boolean(
        !requiresSourceOfFunds ||
        (sourceOfFunds && sourceDocumentType && getUploadedUrl(sourceOfFundsDoc))
    );
    const canContinue =
        !!businessId && averageMonthlyVolumeComplete && websiteComplete && sourceOfFundsComplete;

    const handleInvalidContinue = () => {
        if (submitting) return;

        const requiredErrors = {};
        if (!averageMonthlyVolumeComplete) {
            requiredErrors.averageMonthlyVolume = "This field is required.";
        }
        if (requiresWebsite && !cleanWebsite) {
            requiredErrors.website = "This field is required.";
        } else if (!websiteIsValid) {
            requiredErrors.website = "Enter a valid website address.";
        }
        if (requiresSourceOfFunds && !sourceOfFunds) {
            requiredErrors.sourceOfFunds = "This field is required.";
        }
        if (requiresSourceOfFunds && !sourceDocumentType) {
            requiredErrors.sourceDocumentType = "This field is required.";
        }
        if (requiresSourceOfFunds && !getUploadedUrl(sourceOfFundsDoc)) {
            requiredErrors.sourceOfFundsDoc = "This field is required.";
        }

        setValidationErrors(requiredErrors);
        const firstInvalidField = Object.keys(requiredErrors)[0];
        requestAnimationFrame(() => {
            const field = document.querySelector(
                `[data-onboarding-field="${firstInvalidField}"]`
            );
            field?.scrollIntoView({ behavior: "smooth", block: "center" });
            field?.querySelector("input, button")?.focus({ preventScroll: true });
        });
    };

    const getWalletByCurrency = (currency) =>
        availableWallets.find((wallet) => getWalletCurrency(wallet) === currency);

    const buildFinancialInformationPayload = () => {
        const payload = {};
        const amount = averageMonthlyVolume.replace(/,/g, "").trim();
        const sourceOfFundUrl = getUploadedUrl(sourceOfFundsDoc);

        const walletCurrencies = Array.from(new Set(selectedWallets));

        if (walletCurrencies.length) {
            payload.wallets = walletCurrencies.map((currency) => {
                const wallet = getWalletByCurrency(currency);

                return {
                    currency,
                    flag: getWalletFlag(wallet || currency),
                };
            });
        }

        if (amount) {
            payload.monthlyTransactionVolume = {
                currency: volumeCurrency,
                amount,
            };
        }

        if (requiresSourceOfFunds && sourceOfFundUrl) {
            payload.sourceOfFund = {
                type: selectedSourceOfFunds?.label || sourceOfFunds,
                name: selectedSourceDocumentType?.label || getSourceOfFundName(sourceOfFunds),
                url: sourceOfFundUrl,
            };
        }

        if (cleanWebsite) {
            payload.hasWebsite = true;
            payload.businessWebsite = normalizeUrl(website);
        } else {
            payload.hasWebsite = false;
        }

        return payload;
    };

    const submitFinancialInformation = async () => {
        if (!businessId) {
            setFails(true);
            setErrorMessage("Unable to find business details. Please refresh and try again.");
            return;
        }

        try {
            setSubmitting(true);
            setFails(false);
            setErrorMessage("");
            const payload = buildFinancialInformationPayload();
            await handleUpdateBusinessFinancialInformation(businessId, payload);
            navigate(
                shouldReturnToSummary
                    ? "/onboarding/summary"
                    : "/onboarding/business-ownership/control-person"
            );
        } catch (error) {
            setFails(true);
            setErrorMessage(error?.message || "Unable to update financial information.");
        } finally {
            setSubmitting(false);
        }
    };

    const handleContinue = async () => {
        if (!canContinue || submitting) return;

        await submitFinancialInformation();
    };

    return (
        <OnboardingLayout
            title="Activate account"
            steps={steps}
            currentStep={currentStep}
            currentSubStep={1}
        >
            <div className="max-w-[1100px] md:pl-96">
                <Helmet>
                    <title>Business wallets</title>
                </Helmet>

                <div>
                    <FailHandler success={fails} message={errorMessage} />
                    <h2 className="aku-heading-h2-medium text-[#101828]">Wallets information</h2>

                    <div className="mt-8 max-w-[760px] space-y-4">
                        <div data-onboarding-field="averageMonthlyVolume">
                            <p className="aku-body-small-medium text-[#383B56] mb-2">
                                What other currencies would you need?{" "}
                                <span className="aku-body-small-medium italic">・ Optional</span>
                            </p>
                            <MultiSelectDropdown
                                instanceId="business-verification-wallets"
                                value={selectedWallets}
                                options={walletOptions}
                                placeholder="Select wallets"
                                onChange={setSelectedWallets}
                            />
                        </div>

                        <div>
                            <p className="aku-body-small-medium text-[#101828] mb-2">
                                What&apos;s your average transaction volume per month?{" "}
                                {!requiresAverageMonthlyVolume && (
                                    <span className="aku-body-small-medium italic text-[#383B56]">
                                        ・ Optional
                                    </span>
                                )}
                            </p>
                            <div className={`flex h-[56px] overflow-visible rounded-lg ${
                                validationErrors.averageMonthlyVolume
                                    ? "bg-[#FFF1F3] ring-1 ring-[#F04438]"
                                    : "bg-[#f1f5f9]"
                            }`}>
                                <div className="w-[104px] shrink-0">
                                    <Dropdown
                                        value={volumeCurrency}
                                        options={volumeCurrencyOptions}
                                        onChange={setVolumeCurrency}
                                        className="!mt-0 !rounded-r-none"
                                        dropdownClassName="!min-w-[104px]"
                                    />
                                </div>
                                <input
                                    type="text"
                                    inputMode="numeric"
                                    value={averageMonthlyVolume}
                                    onChange={(event) => handleVolumeChange(event.target.value)}
                                    placeholder="e.g. 100,000"
                                    className="aku-body-normal-regular min-w-0 flex-1 bg-transparent px-4 text-main-dark placeholder:text-main-grey outline-none"
                                />
                                <span className="aku-body-small-medium flex items-center px-5 text-[#383B56]">
                                    monthly
                                </span>
                            </div>
                            {validationErrors.averageMonthlyVolume && (
                                <p className="mt-1 text-xs text-[#D92D20]">
                                    {validationErrors.averageMonthlyVolume}
                                </p>
                            )}
                        </div>

                        <div data-onboarding-field="website">
                            <p className="aku-body-small-medium text-[#101828] mb-2">
                                Business website{" "}
                                {!requiresWebsite && (
                                    <span className="aku-body-small-medium italic text-[#383B56]">
                                        ・ Optional
                                    </span>
                                )}
                            </p>
                            <input
                                type="url"
                                value={website}
                                onChange={(event) => {
                                    setWebsite(event.target.value);
                                    setValidationErrors((current) => ({ ...current, website: "" }));
                                }}
                                placeholder="Enter web address"
                                className={`aku-body-normal-regular h-[56px] w-full rounded-lg bg-[#f1f5f9] px-4 text-main-dark placeholder:text-main-grey outline-none ${
                                    validationErrors.website
                                        ? "!bg-[#FFF1F3] ring-1 ring-[#F04438]"
                                        : ""
                                }`}
                            />
                            {validationErrors.website && (
                                <p className="mt-1 text-sm text-red-600">
                                    {validationErrors.website || "Enter a valid website address."}
                                </p>
                            )}
                        </div>

                        {requiresSourceOfFunds && (
                            <div className="space-y-2">
                                {requiresSourceOfFunds && (
                                    <div>
                                        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                                            <div data-onboarding-field="sourceOfFunds">
                                                <div className="mb-2 flex items-center gap-2">
                                                    <p className="aku-body-small-medium text-[#383B56]">
                                                        Source of funds
                                                    </p>
                                                    <InfoTooltip
                                                        message="Verify the source of your business funds, in line with AML compliance laws"
                                                        className="text-[#7A80AE]"
                                                    />
                                                </div>
                                                <Dropdown
                                                    value={sourceOfFunds}
                                                    onChange={(value) => {
                                                        setSourceOfFunds(value);
                                                        setSourceDocumentType("");
                                                        setSourceOfFundsDoc(null);
                                                        setValidationErrors((current) => ({
                                                            ...current,
                                                            sourceOfFunds: "",
                                                        }));
                                                    }}
                                                    options={SOURCE_OF_FUNDS_OPTIONS}
                                                    placeholder="Select source of funds"
                                                    className="!mt-0"
                                                    dropdownClassName="max-h-56"
                                                    error={validationErrors.sourceOfFunds}
                                                />
                                            </div>

                                            <div data-onboarding-field="sourceDocumentType">
                                                <p className="mb-2 aku-body-small-medium text-[#383B56]">
                                                    Document type
                                                </p>
                                                <Dropdown
                                                    value={sourceDocumentType}
                                                    onChange={(value) => {
                                                        setSourceDocumentType(value);
                                                        setSourceOfFundsDoc(null);
                                                        setValidationErrors((current) => ({
                                                            ...current,
                                                            sourceDocumentType: "",
                                                        }));
                                                    }}
                                                    options={sourceDocumentTypeOptions}
                                                    placeholder={
                                                        sourceOfFunds
                                                            ? "Document type"
                                                            : "Select source of funds first"
                                                    }
                                                    className="!mt-0"
                                                    dropdownClassName="max-h-56"
                                                    error={validationErrors.sourceDocumentType}
                                                    {...(!sourceOfFunds
                                                        ? { disabled: true, readOnly: true }
                                                        : {})}
                                                />
                                            </div>
                                        </div>

                                        <div
                                            data-onboarding-field="sourceOfFundsDoc"
                                            className={`mt-2 rounded-xl bg-white ${
                                                validationErrors.sourceOfFundsDoc
                                                    ? "ring-1 ring-[#F04438]"
                                                    : ""
                                            }`}
                                        >
                                            <FileUploadTile
                                                label="Upload document"
                                                labelClassName="aku-body-small-medium !mb-2"
                                                initialName={sourceOfFundsDoc?.name}
                                                fileId="onboarding-source-of-funds-doc"
                                                disabled={!sourceDocumentType}
                                                directUpload
                                                onUploaded={({ name, url, fileId }) => {
                                                    setSourceOfFundsDoc({ name, url, fileId });
                                                    setValidationErrors((current) => ({
                                                        ...current,
                                                        sourceOfFundsDoc: "",
                                                    }));
                                                }}
                                            />
                                            {validationErrors.sourceOfFundsDoc && (
                                                <p className="mt-1 text-xs text-[#D92D20]">
                                                    {validationErrors.sourceOfFundsDoc}
                                                </p>
                                            )}
                                        </div>

                                        <div className="mt-3 flex items-start gap-2 text-[#475467]">
                                            <InfoCircle size="20" color="#2C53F2" variant="Bold" />
                                            <p className="aku-body-small-regular">
                                                Your document should clearly show your business name
                                                and relevant dates.
                                            </p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    <div className="mt-8 flex max-w-[760px] justify-end gap-4">
                        <button
                            type="button"
                            className="mt-4 text-base font-normal rounded-lg h-12 px-6 flex items-center justify-center text-[#101828]"
                            onClick={handleBack}
                        >
                            Back
                        </button>
                        <button
                            type="button"
                            disabled={submitting}
                            aria-disabled={!canContinue || submitting}
                            className={`mt-4 text-base font-normal rounded-lg h-12 px-6 flex items-center justify-center text-white ${
                                canContinue && !submitting
                                    ? "bg-[#6F00FF]"
                                    : "bg-[#D7C5FF] cursor-not-allowed"
                            }`}
                            onClick={canContinue ? handleContinue : handleInvalidContinue}
                        >
                            {submitting ? <Loader /> : "Continue"}
                        </button>
                    </div>
                </div>
            </div>
        </OnboardingLayout>
    );
};

export default BusinessVerificationWallets;
